import React from "react";
import { Database, Rocket, CheckCircle2, Download } from "lucide-react";
import { Button, Card } from "../ui";

const codeStyle = {
  display: "block",
  background: "var(--bg-tint)",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius-sm)",
  padding: "10px 12px",
  fontFamily: "var(--mono)",
  fontSize: 13,
  color: "var(--ink)",
  marginBottom: 12,
};

const sectionStyle = { fontFamily: "var(--serif)", fontWeight: 500, fontSize: 16, margin: "16px 0 8px" };

export default function DatabaseSetupGuide({ onRetry }) {
  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
      <Card
        style={{
          background: "var(--warn-bg)",
          border: "1px solid var(--warn-border)",
          color: "var(--warn-text)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <Database size={18} />
          <h2 style={{ fontFamily: "var(--serif)", fontWeight: 500, fontSize: 18, margin: 0, color: "inherit" }}>
            Database Setup Required
          </h2>
        </div>
        <p style={{ margin: 0, fontSize: 13 }}>
          It looks like your database isn't set up yet. Follow the steps below to get started.
        </p>
      </Card>

      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Rocket size={20} style={{ color: "var(--primary)" }} />
          <h2 style={{ fontFamily: "var(--serif)", fontWeight: 500, fontSize: 22, margin: 0 }}>Quick Setup Guide</h2>
        </div>
        <p style={{ color: "var(--ink-3)", fontSize: 14 }}>
          This template uses PostgreSQL with Prisma ORM. Follow these steps:
        </p>

        <h3 style={sectionStyle}>1. Copy Environment File</h3>
        <code style={codeStyle}>cp example.env .env</code>

        <h3 style={sectionStyle}>2. Configure Database URL</h3>
        <p style={{ color: "var(--ink-3)", fontSize: 13, margin: "0 0 6px" }}>
          Edit .env and update DATABASE_URL:
        </p>
        <code style={codeStyle}>
          DATABASE_URL="postgresql://username:password@localhost:5432/dbname"
        </code>

        <h3 style={sectionStyle}>3. Setup Database</h3>
        <code style={codeStyle}>npm run db:setup</code>

        <h3 style={sectionStyle}>4. Seed Sample Data (Optional)</h3>
        <code style={codeStyle}>npm run db:seed</code>

        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle2 size={16} style={{ color: "var(--ok)" }} />
          <strong style={{ fontFamily: "var(--serif)", fontWeight: 500, fontSize: 16 }}>
            What You'll Get
          </strong>
        </div>
        <ul style={{ color: "var(--ink-3)", fontSize: 13, margin: "6px 0 16px 22px", lineHeight: 1.6 }}>
          <li>Contact Management System</li>
          <li>Project Management with Team Assignment</li>
          <li>Task Management with Status Tracking</li>
          <li>Sample Data for Testing</li>
        </ul>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Button onClick={onRetry} leftIcon={<Database size={14} />}>
            Check Database Connection
          </Button>
          <Button asChild variant="outline" leftIcon={<Download size={14} />}>
            <a href="https://www.postgresql.org/download/" target="_blank" rel="noreferrer">
              Download PostgreSQL
            </a>
          </Button>
        </div>
      </Card>

      <Card
        style={{
          background: "var(--primary-50)",
          border: "1px solid var(--primary-200)",
          color: "var(--primary-deep)",
        }}
      >
        <p style={{ fontSize: 13, margin: 0 }}>
          <strong>Need help?</strong> Check the README.md file for detailed setup instructions.
        </p>
      </Card>
    </div>
  );
}
